'use client';

import { type MouseEvent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { WishlistButton } from '@packages/shared/src/ui/WishlistButton/WishlistButton';
import type { WishlistButtonProps } from '@packages/shared/src/ui/WishlistButton/WishlistButton';
import type { AvailabilityState } from '@packages/gql/generated/shopping/graphql';
import {
  type GlycerinAddToWishListPayload,
  type GlycerinRemoveFromWishListPayload,
} from '@packages/tracking/src/types/payloads';
import {
  type GTMEventGlycerinAddToWishList,
  type GTMEventGlycerinRemoveFromWishList,
} from '@packages/tracking/src/types/events';
import { type GlycerinWishListProduct } from '@packages/tracking/src/types/types';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { getBridge } from '@packages/shared/src/utils/getBridge';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { AddToWishlistDocument } from '@packages/gql/generated/shopping/AddToWishlistDocument';
import { RemoveFromWishlistDocument } from '@packages/gql/generated/shopping/RemoveFromWishlistDocument';
import { WishlistSkusDocument } from '@packages/gql/generated/shopping/WishlistSkusDocument';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { useSession } from '@packages/shared/src/hooks/useSession/useSession';
import { wishlistMessages } from './messages';

export type WishlistTrackingProps = Omit<GlycerinAddToWishListPayload, 'addedProducts'> &
  Omit<GlycerinRemoveFromWishListPayload, 'removedProducts'> & {
    products: GlycerinWishListProduct[];
  };

export type WishlistButtonWithApiProps = {
  /** The sku of the desired product */
  sku: string;
  /** Availability-state as string. Can be undefined if state is unknown. Can also be state 'UNSELECTED' which is identical to undefined. */
  availability?: AvailabilityState;
  /** name of the product to be added */
  productName?: string;
  /** icon only version
   * @default true
   */
  iconOnly?: boolean;
  trackingProps?: WishlistTrackingProps;
} & Omit<WishlistButtonProps, 'isActive'>;

/* GraphQL */ `
  query WishlistSkus($locale: String!) {
    wishlist(locale: $locale) {
      lineItems {
        sku
      }
      count
    }
  }
`;

// TODO: refactor button to only use one sku for both tracking and api calls
/**
 * WishlistButtonWithApi connects to wishlist api, sets button state and displays a Notification on change
 */
export const WishlistButtonWithApi = ({
  sku,
  productName,
  availability,
  iconOnly = true,
  trackingProps,
  variant,
  ...rest
}: WishlistButtonWithApiProps) => {
  const { formatMessage } = useIntl();
  // The messages select their generic wording on the key `undefined`.
  const messageValues = { productName: productName ?? 'undefined' };
  const dispatchGTMEvent = useTracking();
  const { locale } = useI18n();
  const { jwtToken } = useSession();
  const [{ data, fetching }] = useAuthQuery({
    query: WishlistSkusDocument,
    variables: { locale },
  });
  const [, executeAddToWishlist] = useAuthMutation(AddToWishlistDocument);
  const [, executeRemoveFromWishlist] = useAuthMutation(RemoveFromWishlistDocument);
  const [isActive, setIsActive] = useState(false);
  const { addNotification } = useNotification();

  const { products, custom, name, variantIds, wishListId } = trackingProps ?? {};

  useEffect(() => {
    if (data && !fetching) {
      const wishlistSKUs = data.wishlist?.lineItems.map((item) => item.sku) ?? [];
      setIsActive(wishlistSKUs.includes(sku));
    }
  }, [data, fetching, sku]);

  const clickHandler = async (event: MouseEvent<HTMLButtonElement>) => {
    // the button is often rendered inside clickable product cards, whose click handlers must not
    // treat adding/removing a product to the wishlist as a click on the product card itself
    // this would fire unnecessary product click events
    event.stopPropagation();

    if (isActive) {
      try {
        const result = await executeRemoveFromWishlist({ locale, sku });
        if (result.data?.removeFromWishlist) {
          addNotification({
            severity: 'success',
            message: formatMessage(wishlistMessages.removeSuccess, messageValues),
          });
          getBridge().updateWishlist();
          if (products && products.length > 0) {
            // sending undefined or an empty array will cause the event to be invalid in nitro sniffer
            dispatchGTMEvent<GTMEventGlycerinRemoveFromWishList>({
              event: 'RemoveFromWishList',
              RemoveFromWishListData: {
                removedProducts: products,
                custom,
                name,
                variantIds,
                wishListId,
              },
            });
          }
        } else {
          throw new Error();
        }
      } catch {
        addNotification({
          severity: 'error',
          message: formatMessage(wishlistMessages.removeError, messageValues),
        });
      }
    } else {
      try {
        const result = await executeAddToWishlist({
          locale,
          sku,
          unspecified: availability === 'UNSELECTED',
        });
        if (result.data?.addToWishlist) {
          addNotification({
            severity: 'success',
            message: formatMessage(wishlistMessages.addSuccess, messageValues),
          });
          getBridge().updateWishlist();
          // sending undefined or an empty array will cause the wishlist payload to be invalid in nitro sniffer
          if (products && products.length > 0) {
            dispatchGTMEvent<GTMEventGlycerinAddToWishList>({
              event: 'AddToWishList',
              AddToWishListData: {
                addedProducts: products,
                custom,
                name,
                variantIds,
                wishListId,
              },
            });
          }
        } else {
          throw new Error();
        }
      } catch {
        addNotification({
          severity: 'error',
          message: formatMessage(wishlistMessages.addError, messageValues),
        });
      }
    }
  };
  const isLoading = !jwtToken || fetching;

  return (
    <div
      //  workaround: to be provided here on the div wrapper to avoid the screen reader, while keeping the hover tooltip intact. Aria-label is already provided on the button.
      title={
        isLoading
          ? undefined
          : formatMessage(
              isActive ? wishlistMessages.removeTitle : wishlistMessages.addTitle,
              messageValues,
            )
      }
    >
      <WishlistButton
        {...rest}
        isActive={isActive}
        onClick={isLoading ? undefined : clickHandler}
        loading={isLoading}
        variant={variant}
        disabled={isLoading}
        data-testid={`wishlist-button-${isActive ? 'active' : 'inactive'}`}
        aria-label={
          isLoading
            ? undefined
            : formatMessage(isActive ? wishlistMessages.ariaRemove : wishlistMessages.ariaAdd)
        }
      >
        {iconOnly
          ? null
          : formatMessage(isActive ? wishlistMessages.removeLabel : wishlistMessages.addLabel)}
      </WishlistButton>
    </div>
  );
};
